import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Input,
  Switch,
  Button,
  Spin,
  notification,
  Select,
  Checkbox,
  Modal,
  Tag,
  Tooltip,
  Progress,
  InputNumber,
  Alert,
  Segmented,
} from "antd";
import {
  PhoneOutlined,
  PlusOutlined,
  CheckCircleFilled,
  ThunderboltOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  AudioOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import firebaseApp from "firebase/app";
import firebase from "FirebaseConfig.js";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import ReactInputVerificationCode from "react-input-verification-code";

import {
  fetchSettings,
  updateSettings,
  fetchVerifiedNumbers,
  confirmNumberVerification,
  startTestCall,
  fetchTestCall,
  fetchElevenLabsVoices,
  fetchCallSurveys,
  startBrowserTest,
  attachBrowserConversationWhenReady,
  fetchTestCallRecording,
} from "./service";
import { acquireMicrophone, startBrowserVoiceSession } from "./browserVoiceSession";
import {
  PERSONA_OPTIONS,
  TEST_CALL_MAX_PER_HOUR,
  SUPPORTED_LANGUAGES,
  getLanguageLabel,
  DEFAULT_CALL_LIMITS,
  MAX_ATTEMPT_PERIOD_OPTIONS,
  CALL_LANGUAGES,
} from "./constants";
import { languagesMayMismatch, languageForUi } from "./languageUtils";
import { markSetupTestComplete } from "./SetupTab";
import {
  estimateSpeechDuration,
  exceedsGreetingLimit,
  GREETING_MAX_SECONDS,
} from "./speechDuration";
import {
  isSpeechPreviewSupported,
  isSpeechPreviewSpeaking,
  speakGreetingPreview,
  stopSpeechPreview,
  fillGreetingForPreview,
  playBase64Mp3,
  unlockAudioPlayback,
} from "./voicePreview";
import { resolveSonicApiError, isSonicSubscriptionError } from "./sonicApiError";
import { resolveVoiceEngine } from "util/sonicVoiceApi";
import styles from "./style.module.scss";
import { BrandTag } from "pages/SurveyManagement/phoneTriggerShared";

const toSonicErrorState = (res, t, fallbackKey) => {
  if (!res?.error) return null;
  return {
    message: resolveSonicApiError(res, t, fallbackKey),
    subscription: isSonicSubscriptionError(res),
  };
};
const VOICE_ENGINE_OPTIONS = [
  { value: "auto", labelKey: "sonic.voice_engine_auto" },
  { value: "elevenlabs", labelKey: "sonic.voice_engine_elevenlabs" },
  { value: "gemini_live", labelKey: "sonic.voice_engine_gemini" },
];

const { TextArea } = Input;
const { Option } = Select;

const POLL_MS = 3000;
const DONE_STATES = ["completed", "failed"];
// Instrument types whose questions the phone agent can ask.
const CALL_SURVEY_TYPES = ["survey", "ai_voice"];
const CALL_RESULT_KEYS = {
  no_answer: "sonic.call_result_no_answer",
  busy: "sonic.call_result_busy",
  voicemail: "sonic.call_result_voicemail",
};

const callUnavailableKey = (reason) =>
  reason === "phone_line_not_connected"
    ? "sonic.voice_line_not_connected"
    : "sonic.voice_coming_soon";

const formatAnswerValue = (value) =>
  Array.isArray(value) ? value.join(", ") : String(value);
const CALL_ERROR_KEYS = {
  provider_error: "sonic.call_error_provider",
  test_call_limit: "sonic.call_error_limit",
  number_not_verified: "sonic.call_error_not_verified",
  consent_required: "sonic.call_error_consent",
};
const SENTIMENT_COLORS = { positive: "purple", neutral: "neutral", negative: "pink" };
const VERIFY_TIMER_SEC = 120;
const PHONE_PROVIDER_ID = "phone";

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

const emptyQuota = {
  test_calls_used: 0,
  test_calls_max: TEST_CALL_MAX_PER_HOUR,
  test_calls_remaining: TEST_CALL_MAX_PER_HOUR,
};

const pickQuota = (r) => {
  if (!r || r.test_calls_remaining === undefined) return null;
  return {
    test_calls_used: r.test_calls_used,
    test_calls_max: r.test_calls_max,
    test_calls_remaining: r.test_calls_remaining,
  };
};

const hasPhoneProvider = (user) =>
  (user?.providerData || []).some((p) => p?.providerId === PHONE_PROVIDER_ID);

const unlinkPhoneProvider = async (user) => {
  if (!user || !hasPhoneProvider(user)) return;
  await user.unlink(PHONE_PROVIDER_ID);
  await user.getIdToken(true);
};

/**
 * Voice Agent configuration (left) + the "Test your Voice Agent" sandbox
 * (right). Phone verification uses Firebase Phone Auth (same stack as 2FA):
 * linkWithCredential proves SMS ownership, backend checks the Firebase ID token.
 */
// 0 is "no limit"; the call service caps it at 30 minutes.
const DURATION_OPTIONS = [120, 300, 600, 0];

const VoiceAgentTab = ({ active, focusTest, embedded = false }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const testCardRef = useRef(null);
  const callTimerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState({
    greeting: "",
    language: languageForUi(i18n.language),
    voice_engine: "auto",
    elevenlabs_voice_id: "",
    escalate_to_human: false,
    transfer_number: "",
    consent_logging: true,
    max_call_duration: 300,
    retry_enabled: false,
    retry_hours: 4,
    ...DEFAULT_CALL_LIMITS,
  });
  const [callElapsed, setCallElapsed] = useState(0);

  const [numbers, setNumbers] = useState([]);
  const [quota, setQuota] = useState(emptyQuota);
  const [voiceTestEnabled, setVoiceTestEnabled] = useState(false);
  const [callUnavailableReason, setCallUnavailableReason] = useState(null);
  const [browserTestEnabled, setBrowserTestEnabled] = useState(false);
  // "phone" | "browser"; unset until picked, then it follows whether a phone line exists.
  const [testMode, setTestMode] = useState(null);
  const [live, setLive] = useState(false);
  const [liveLines, setLiveLines] = useState([]);
  const sessionRef = useRef(null);
  const [surveys, setSurveys] = useState([]);
  const [voiceTtsEnabled, setVoiceTtsEnabled] = useState(false);
  const [elevenLabsVoices, setElevenLabsVoices] = useState([]);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [voicesLoadError, setVoicesLoadError] = useState(null);
  const [sonicAccessError, setSonicAccessError] = useState(null);
  const [form, setForm] = useState({
    context: "",
    persona: PERSONA_OPTIONS[0].value,
    phone: undefined,
    instrumentId: undefined,
    // Unset until picked: the call follows the agent's language.
    callLanguage: undefined,
    consent: false,
  });
  const [starting, setStarting] = useState(false);
  const [call, setCall] = useState(null);
  const [preview, setPreview] = useState(null);
  // Replay of a finished call: the decoded recording and which line is playing.
  const recordingRef = useRef({ callId: null, buffer: null, ctx: null, node: null });
  const [recordingLoading, setRecordingLoading] = useState(false);
  const [playingKey, setPlayingKey] = useState(null);
  const [previewSpeaking, setPreviewSpeaking] = useState(false);
  const pollRef = useRef(null);
  const recaptchaRef = useRef(null);

  const [verify, setVerify] = useState({
    open: false,
    step: "phone",
    phone: "",
    label: "",
    code: "",
    verificationId: "",
    sending: false,
    confirming: false,
    timeLeft: 0,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setSonicAccessError(null);
    const [settingsRes, numbersRes, surveysRes] = await Promise.all([
      fetchSettings(),
      fetchVerifiedNumbers(),
      fetchCallSurveys(),
    ]);
    // Orgs without Feedback Capture get an error here; they simply have no survey to pick.
    setSurveys(
      (surveysRes?.instruments || []).filter(
        (i) => CALL_SURVEY_TYPES.includes(i.type) && i.status !== "closed"
      )
    );
    const accessErr =
      toSonicErrorState(settingsRes, t) || toSonicErrorState(numbersRes, t);
    if (accessErr) {
      setSonicAccessError(accessErr);
    }
    if (settingsRes && !settingsRes.error) {
      const vp = (settingsRes.settings && settingsRes.settings.voice_provider_config) || {};
      setCfg({
        greeting: vp.greeting != null ? vp.greeting : t("sonic.greeting_placeholder"),
        language: vp.language || languageForUi(i18n.language),
        voice_engine: vp.voice_engine || "auto",
        elevenlabs_voice_id: vp.elevenlabs_voice_id || "",
        brand_name: vp.brand_name || "",
        escalate_to_human: !!vp.escalate_to_human,
        transfer_number: vp.transfer_number || "",
        consent_logging: vp.consent_logging !== false,
        max_call_duration: vp.max_call_duration != null ? vp.max_call_duration : 300,
        retry_enabled: !!vp.retry_enabled,
        retry_hours: vp.retry_hours != null ? vp.retry_hours : 4,
        calling_hours_enabled: vp.calling_hours_enabled !== false,
        calling_hours_start: vp.calling_hours_start || DEFAULT_CALL_LIMITS.calling_hours_start,
        calling_hours_end: vp.calling_hours_end || DEFAULT_CALL_LIMITS.calling_hours_end,
        exclude_weekends: vp.exclude_weekends !== false,
        exclude_public_holidays: !!vp.exclude_public_holidays,
        max_attempts_per_contact:
          vp.max_attempts_per_contact != null
            ? vp.max_attempts_per_contact
            : DEFAULT_CALL_LIMITS.max_attempts_per_contact,
        max_attempts_period: vp.max_attempts_period || DEFAULT_CALL_LIMITS.max_attempts_period,
      });
    }
    if (numbersRes && !numbersRes.error) {
      setNumbers(numbersRes.numbers || []);
      setVoiceTestEnabled(!!numbersRes.voice_test_enabled);
      setCallUnavailableReason(numbersRes.voice_call_unavailable_reason || null);
      setBrowserTestEnabled(!!numbersRes.voice_browser_test_enabled);
      setVoiceTtsEnabled(!!numbersRes.voice_tts_enabled);
      const q = pickQuota(numbersRes);
      if (q) setQuota(q);
    }
    setLoading(false);
  }, [t]);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  useEffect(() => {
    if (!active || !focusTest || !testCardRef.current) return undefined;
    const timer = setTimeout(() => {
      testCardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
    return () => clearTimeout(timer);
  }, [active, focusTest]);

  useEffect(
    () => () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (sessionRef.current) sessionRef.current.stop();
      const rec = recordingRef.current;
      if (rec.ctx) rec.ctx.close().catch(() => {});
      stopSpeechPreview();
    },
    []
  );

  useEffect(() => {
    if (!verify.open || verify.timeLeft <= 0) return undefined;
    const timer = setTimeout(() => {
      setVerify((v) => ({ ...v, timeLeft: Math.max(0, v.timeLeft - 1) }));
    }, 1000);
    return () => clearTimeout(timer);
  }, [verify.open, verify.timeLeft]);

  useEffect(() => {
    if (!active || resolveVoiceEngine(cfg) !== "elevenlabs") {
      setElevenLabsVoices([]);
      setVoicesLoadError(null);
      return undefined;
    }
    let cancelled = false;
    setVoicesLoading(true);
    setVoicesLoadError(null);
    fetchElevenLabsVoices(cfg.language).then((res) => {
      if (cancelled) return;
      setVoicesLoading(false);
      if (res && !res.error && Array.isArray(res.voices)) {
        setElevenLabsVoices(res.voices);
        setVoicesLoadError(null);
        const ids = res.voices.map((v) => v.voice_id);
        if (!cfg.elevenlabs_voice_id || !ids.includes(cfg.elevenlabs_voice_id)) {
          patch({ elevenlabs_voice_id: res.default_voice_id || res.voices[0]?.voice_id || "" });
        }
      } else {
        setElevenLabsVoices([]);
        setVoicesLoadError(
          toSonicErrorState(res, t, "sonic.elevenlabs_voices_error")
        );
      }
    });
    return () => {
      cancelled = true;
    };
  }, [active, cfg.language, cfg.voice_engine]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = (p) => setCfg((c) => ({ ...c, ...p }));
  const patchForm = (p) => setForm((f) => ({ ...f, ...p }));
  const resolvedVoiceEngine = resolveVoiceEngine(cfg);
  const useElevenLabsUi = resolvedVoiceEngine === "elevenlabs";
  const showElevenLabsPicker = useElevenLabsUi;
  const selectedElevenLabsVoice = elevenLabsVoices.find(
    (voice) => voice.voice_id === cfg.elevenlabs_voice_id
  );
  const elevenLabsVoiceOptions = elevenLabsVoices.map((voice) => ({
    value: voice.voice_id,
    label: voice.name,
  }));

  const resetRecaptcha = () => {
    if (recaptchaRef.current) {
      try {
        recaptchaRef.current.clear();
      } catch (e) {
        // ignore stale verifier cleanup
      }
    }
    recaptchaRef.current = null;
  };

  const ensureRecaptcha = () => {
    if (!recaptchaRef.current) {
      recaptchaRef.current = new firebaseApp.auth.RecaptchaVerifier(
        "sonic-voice-verify-btn",
        { size: "invisible" }
      );
    }
    return recaptchaRef.current;
  };

  const canPreviewSpeech =
    !!fillGreetingForPreview(cfg.greeting) &&
    ((useElevenLabsUi) ||
      (resolvedVoiceEngine === "gemini_live" && isSpeechPreviewSupported()) ||
      isSpeechPreviewSupported());

  const toggleSpeechPreview = () => {
    unlockAudioPlayback();

    if (previewSpeaking || isSpeechPreviewSpeaking()) {
      stopSpeechPreview();
      setPreviewSpeaking(false);
      return;
    }

    const text = fillGreetingForPreview(cfg.greeting);
    if (!text) {
      notification.warning({ message: t("sonic.tts_empty") });
      return;
    }

    speakGreetingPreview(cfg.greeting, {
      languageCode: cfg.language,
      voiceId: cfg.elevenlabs_voice_id,
      voiceEngine: cfg.voice_engine || "auto",
      preferElevenLabs: useElevenLabsUi,
      onStart: () => setPreviewSpeaking(true),
      onEnd: () => setPreviewSpeaking(false),
      onError: (reason) => {
        setPreviewSpeaking(false);
        if (reason === "unsupported") {
          notification.warning({ message: t("sonic.tts_unsupported") });
        } else if (reason === "empty") {
          notification.warning({ message: t("sonic.tts_empty") });
        } else if (reason === "api") {
          notification.error({ message: t("sonic.tts_elevenlabs_error") });
        } else {
          notification.error({ message: t("sonic.tts_error") });
        }
      },
    });
  };

  const save = async () => {
    setSaving(true);
    const res = await updateSettings({
      voice_provider_config: {
        greeting: cfg.greeting,
        language: cfg.language,
        voice_engine: cfg.voice_engine || "auto",
        elevenlabs_voice_id: cfg.elevenlabs_voice_id || undefined,
        brand_name: (cfg.brand_name || "").trim() || undefined,
        escalate_to_human: cfg.escalate_to_human,
        transfer_number: cfg.transfer_number,
        consent_logging: cfg.consent_logging,
        max_call_duration: cfg.max_call_duration,
        retry_enabled: cfg.retry_enabled,
        retry_hours: cfg.retry_hours,
        calling_hours_enabled: cfg.calling_hours_enabled,
        calling_hours_start: cfg.calling_hours_start,
        calling_hours_end: cfg.calling_hours_end,
        exclude_weekends: cfg.exclude_weekends,
        exclude_public_holidays: cfg.exclude_public_holidays,
        max_attempts_per_contact: cfg.max_attempts_per_contact,
        max_attempts_period: cfg.max_attempts_period,
      },
    });
    setSaving(false);
    if (res && !res.error) {
      notification.success({ message: t("sonic.settings_saved") });
    } else {
      notification.error({ message: (res && res.error) || t("sonic.action_failed") });
    }
  };

  const startPolling = (id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const poll = async () => {
      const res = await fetchTestCall(id);
      if (res && !res.error && res.call) {
        setCall(res.call);
        if (DONE_STATES.includes(res.call.status)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (callTimerRef.current) {
            clearInterval(callTimerRef.current);
            callTimerRef.current = null;
          }
          if (res.call.status === "completed") {
            markSetupTestComplete();
            if (res.call.preview_audio_base64) {
              playBase64Mp3(res.call.preview_audio_base64);
            }
          }
        }
      }
    };
    pollRef.current = setInterval(poll, POLL_MS);
    poll();
  };

  const runTestCall = async () => {
    setPreview(null);
    resetRecording();
    setCall(null);
    setStarting(true);
    const res = await startTestCall({
      phone: form.phone,
      context: form.context.trim(),
      persona: form.persona,
      language: form.callLanguage || cfg.language,
      // Summary, answers and transcript come back in the user's own language.
      report_language: i18n.language,
      consent: form.consent,
      instrument_id: form.instrumentId || undefined,
    });
    setStarting(false);

    const q = pickQuota(res);
    if (q) setQuota(q);

    if (res && res.coming_soon) {
      setPreview(res.preview_transcript || "");
      notification.info({ message: t(callUnavailableKey(res.reason)) });
      return;
    }
    if (!res || res.error) {
      const errorKey = CALL_ERROR_KEYS[res?.reason];
      notification.error({
        message: errorKey ? t(errorKey) : resolveSonicApiError(res, t, "sonic.action_failed"),
      });
      return;
    }
    setCall(res.call);
    setCallElapsed(0);
    notification.success({ message: t("sonic.test_call_started") });
    if (res.call && res.call.id) {
      startPolling(res.call.id);
      if (callTimerRef.current) clearInterval(callTimerRef.current);
      callTimerRef.current = setInterval(() => {
        setCallElapsed((s) => s + 1);
      }, 1000);
    }
  };

  const stopRecordingPlayback = () => {
    const rec = recordingRef.current;
    if (rec.node) {
      try {
        rec.node.stop();
      } catch (e) {
        // already ended
      }
      rec.node = null;
    }
    setPlayingKey(null);
  };

  const resetRecording = () => {
    stopRecordingPlayback();
    recordingRef.current.callId = null;
    recordingRef.current.buffer = null;
  };

  const loadRecording = async (callId) => {
    const rec = recordingRef.current;
    if (rec.buffer && rec.callId === callId) return rec.buffer;
    setRecordingLoading(true);
    const data = await fetchTestCallRecording(callId).catch(() => null);
    setRecordingLoading(false);
    if (!data) return null;
    if (!rec.ctx) rec.ctx = new (window.AudioContext || window.webkitAudioContext)();
    try {
      rec.buffer = await rec.ctx.decodeAudioData(data);
      rec.callId = callId;
      return rec.buffer;
    } catch (e) {
      return null;
    }
  };

  // Plays the whole call, or one line from its start to the next line's start.
  const playRecording = async (key, start = 0, end = null) => {
    if (playingKey === key) {
      stopRecordingPlayback();
      return;
    }
    stopRecordingPlayback();
    const buffer = await loadRecording(call.id);
    if (!buffer) {
      notification.error({ message: t("sonic.recording_unavailable") });
      return;
    }
    const rec = recordingRef.current;
    if (rec.ctx.state === "suspended") await rec.ctx.resume();
    const node = rec.ctx.createBufferSource();
    node.buffer = buffer;
    node.connect(rec.ctx.destination);
    const offset = Math.max(0, Math.min(start || 0, buffer.duration));
    node.onended = () => {
      if (recordingRef.current.node === node) {
        recordingRef.current.node = null;
        setPlayingKey(null);
      }
    };
    if (end != null && end > offset) node.start(0, offset, end - offset);
    else node.start(0, offset);
    rec.node = node;
    setPlayingKey(key);
  };

  const stopTimer = () => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
  };

  const runBrowserTest = async () => {
    setPreview(null);
    resetRecording();
    setCall(null);
    setLiveLines([]);
    setStarting(true);
    let stream;
    try {
      stream = await acquireMicrophone();
    } catch (err) {
      setStarting(false);
      notification.error({ message: t("sonic.browser_mic_denied") });
      return;
    }
    const res = await startBrowserTest({
      context: form.context.trim(),
      persona: form.persona,
      language: form.callLanguage || cfg.language,
      report_language: i18n.language,
      instrument_id: form.instrumentId || undefined,
    });
    setStarting(false);
    const q = pickQuota(res);
    if (q) setQuota(q);
    if (!res || res.error || !res.signed_url) {
      stream.getTracks().forEach((track) => track.stop());
      const errorKey = res && res.coming_soon ? callUnavailableKey(res.reason) : CALL_ERROR_KEYS[res?.reason];
      notification.error({
        message: errorKey ? t(errorKey) : resolveSonicApiError(res, t, "sonic.action_failed"),
      });
      return;
    }

    const callId = res.call.id;
    setCall(res.call);
    setLive(true);
    setCallElapsed(0);
    stopTimer();
    callTimerRef.current = setInterval(() => setCallElapsed((s) => s + 1), 1000);
    sessionRef.current = startBrowserVoiceSession({
      stream,
      signedUrl: res.signed_url,
      dynamicVariables: res.dynamic_variables,
      onConversationId: (conversationId) => attachBrowserConversationWhenReady(callId, conversationId),
      onLine: (role, text) => {
        if (!text) return;
        setLiveLines((lines) => [...lines, { key: `${Date.now()}-${lines.length}`, role, text }]);
      },
      onEnd: () => {
        sessionRef.current = null;
        setLive(false);
        stopTimer();
        // The transcript, answers and survey response arrive once ElevenLabs has analysed the call.
        startPolling(callId);
      },
      onError: () => notification.error({ message: t("sonic.browser_connection_error") }),
    });
  };

  const stopBrowserTest = () => {
    if (sessionRef.current) sessionRef.current.stop();
  };

  const openVerify = () => {
    resetRecaptcha();
    setVerify({
      open: true,
      step: "phone",
      phone: "",
      label: "",
      code: "",
      verificationId: "",
      sending: false,
      confirming: false,
      timeLeft: 0,
    });
  };

  const closeVerify = () => {
    resetRecaptcha();
    setVerify((v) => ({ ...v, open: false }));
  };

  const backToPhoneStep = () => {
    resetRecaptcha();
    setVerify((v) => ({
      ...v,
      step: "phone",
      code: "",
      verificationId: "",
      timeLeft: 0,
    }));
  };

  const defaultPhoneCountry = (i18n.language || "").startsWith("tr") ? "TR" : "US";

  const sendCode = async () => {
    const phone = (verify.phone || "").trim();
    if (!phone) return;

    const user = firebase.auth().currentUser;
    if (!user) {
      notification.error({ message: t("sonic.verify_login_required") });
      return;
    }

    setVerify((v) => ({ ...v, sending: true }));
    try {
      const provider = new firebaseApp.auth.PhoneAuthProvider();
      const verificationId = await provider.verifyPhoneNumber(
        phone,
        ensureRecaptcha()
      );
      setVerify((v) => ({
        ...v,
        step: "code",
        verificationId,
        sending: false,
        timeLeft: VERIFY_TIMER_SEC,
        code: "",
      }));
      notification.success({ message: t("sonic.code_sent") });
    } catch (e) {
      resetRecaptcha();
      setVerify((v) => ({ ...v, sending: false }));
      notification.error({
        message: (e && e.message) || t("sonic.action_failed"),
      });
    }
  };

  const resendCode = async () => {
    if (verify.timeLeft > 0) return;
    resetRecaptcha();
    await sendCode();
  };

  const confirmCode = async () => {
    const user = firebase.auth().currentUser;
    if (!user) {
      notification.error({ message: t("sonic.verify_login_required") });
      return;
    }
    if (!verify.verificationId || verify.code.trim().length < 6) return;

    setVerify((v) => ({ ...v, confirming: true }));
    let linked = false;
    try {
      await unlinkPhoneProvider(user);
      const cred = firebaseApp.auth.PhoneAuthProvider.credential(
        verify.verificationId,
        verify.code.trim()
      );
      await user.linkWithCredential(cred);
      linked = true;

      const linkedPhone = user.phoneNumber || verify.phone;
      const firebaseIdToken = await user.getIdToken(true);
      const res = await confirmNumberVerification(
        linkedPhone,
        verify.label.trim(),
        firebaseIdToken
      );

      if (!res || res.error || !res.verified) {
        throw new Error((res && res.error) || t("sonic.action_failed"));
      }

      const list = res.numbers || numbers;
      setNumbers(list);
      const added =
        list.find((n) => n && n.phone === linkedPhone) ||
        list.find((n) => n && n.phone) ||
        {};
      patchForm({ phone: added.phone });
      closeVerify();
      notification.success({ message: t("sonic.number_verified") });
    } catch (e) {
      notification.error({
        message: (e && e.message) || t("sonic.action_failed"),
      });
    } finally {
      if (linked) {
        try {
          await unlinkPhoneProvider(user);
        } catch (e) {
          // best-effort: phone should not stay on the Firebase account
        }
      }
      setVerify((v) => ({ ...v, confirming: false }));
    }
  };

  if (loading) {
    return (
      <div className={styles.centeredSpin}>
        <Spin />
      </div>
    );
  }

  const testModeValue = testMode || (voiceTestEnabled ? "phone" : "browser");
  const canStartBrowser =
    browserTestEnabled &&
    (!!form.context.trim() || !!form.instrumentId) &&
    quota.test_calls_remaining > 0 &&
    !starting &&
    !live &&
    !(call && call.status === "in_progress");

  const canStart =
    !!form.phone &&
    (!!form.context.trim() || !!form.instrumentId) &&
    form.consent &&
    quota.test_calls_remaining > 0 &&
    !starting &&
    !(call && call.status === "in_progress");

  const quotaPct =
    quota.test_calls_max > 0
      ? Math.round((quota.test_calls_remaining / quota.test_calls_max) * 100)
      : 0;

  const formatCallDuration = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const callInFlight = starting || live || (call && !DONE_STATES.includes(call.status));

  const renderTranscript = () => {
    if (live) {
      return liveLines.length ? (
        <div className={styles.transcriptBody}>
          {liveLines.map((line) => (
            <div key={line.key} className={styles.liveLine}>
              <span className={styles.liveSpeaker}>
                {line.role === "agent" ? t("sonic.browser_agent") : t("sonic.browser_you")}:
              </span>{" "}
              {line.text}
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.transcriptIdle}>{t("sonic.browser_listening")}</div>
      );
    }
    if (preview != null) {
      return (
        <div className={styles.transcriptBody}>
          <Tag color="purple" className={styles.previewTag}>
            {t("sonic.transcript_preview_label")}
          </Tag>
          <pre className={styles.transcriptText}>{preview}</pre>
        </div>
      );
    }
    if (!call) {
      return (
        <div className={styles.transcriptIdle}>{t("sonic.transcript_idle")}</div>
      );
    }
    if (call.status === "in_progress") {
      return (
        <div className={styles.transcriptIdle}>
          <Spin indicator={<LoadingOutlined spin />} />
          <span className={styles.transcriptCalling}>
            {call.mode === "browser" ? t("sonic.browser_analyzing") : t("sonic.calling")}
          </span>
        </div>
      );
    }
    if (call.status === "failed") {
      const resultKey = CALL_RESULT_KEYS[call.failure_reason];
      return (
        <div className={styles.transcriptIdle}>
          {t("sonic.call_failed")}
          {resultKey ? <span>{t(resultKey)}</span> : null}
        </div>
      );
    }
    return (
      <div className={styles.transcriptBody}>
        <div className={styles.transcriptMeta}>
          {call.sentiment ? (
            <BrandTag tone={SENTIMENT_COLORS[call.sentiment]}>
              {t(`sonic.sentiment_${call.sentiment}`)}
            </BrandTag>
          ) : null}
          {call.duration_seconds != null ? (
            <span className={styles.transcriptDuration}>
              {call.duration_seconds}s
            </span>
          ) : null}
        </div>
        {call.turns && call.turns.length > 0 ? (
          <div className={styles.turnList}>
            {call.has_recording && (
              <Button
                size="small"
                icon={playingKey === "all" ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                loading={recordingLoading && playingKey === null}
                onClick={() => playRecording("all")}
                className={styles.playAllBtn}
              >
                {t("sonic.recording_play_all")}
              </Button>
            )}
            {call.turns.map((turn, idx) => {
              const key = `turn-${idx}`;
              const next = call.turns.slice(idx + 1).find((x) => x.start != null);
              return (
                <div
                  key={key}
                  className={turn.role === "customer" ? styles.turnCustomer : styles.turnAgent}
                >
                  <span className={styles.liveSpeaker}>
                    {turn.role === "agent" ? t("sonic.browser_agent") : t("sonic.transcript_customer")}:
                  </span>{" "}
                  {turn.text}
                  {call.has_recording && turn.start != null && (
                    <Button
                      type="link"
                      size="small"
                      icon={playingKey === key ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                      onClick={() => playRecording(key, turn.start, next ? next.start : null)}
                    >
                      {formatCallDuration(Math.floor(turn.start))}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <pre className={styles.transcriptText}>{call.transcript}</pre>
        )}
        {call.survey_answers && call.survey_answers.length > 0 ? (
          <div className={styles.surveyAnswers}>
            <div className={styles.surveyAnswersTitle}>
              {t("sonic.survey_answers_title")}
              {call.survey_response_id ? (
                <Tag color="purple" className={styles.previewTag}>
                  {t("sonic.survey_response_saved")}
                </Tag>
              ) : null}
            </div>
            {call.survey_answers.map((a) => (
              <div key={a.question_id} className={styles.surveyAnswerRow}>
                <span className={styles.surveyAnswerQuestion}>{a.question_text}</span>
                <span className={styles.surveyAnswerValue}>{formatAnswerValue(a.value)}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  const greetingDuration = estimateSpeechDuration(cfg.greeting, cfg.language);
  const greetingTooLong = exceedsGreetingLimit(cfg.greeting, cfg.language);

  return (
    <div className={styles.tabInner}>
    <div
      className={[
        styles.voiceLayout,
        embedded ? styles.voiceLayoutEmbedded : "",
      ].join(" ")}
    >
      <div className={`${styles.voiceConfigCol} ${styles.sonicPanel}`}>
        {sonicAccessError && (
          <Alert
            type={sonicAccessError.subscription ? "warning" : "error"}
            showIcon
            className={styles.voiceAccessAlert}
            message={
              sonicAccessError.subscription
                ? t("sonic.subscription_required_title")
                : t("sonic.voice_load_error_title")
            }
            description={sonicAccessError.message}
          />
        )}
        <div className={styles.voiceHeader}>
          <h3>{t("sonic.voice_agent_title")}</h3>
        </div>

        <h4 className={styles.voiceSectionTitle}>{t("sonic.voice_section_says")}</h4>
        <div className={styles.previewCard}>
          <div className={styles.voiceSettingsRow}>
            <div className={styles.voiceSettingsField}>
              <label className={styles.fieldLabel}>{t("sonic.language")}</label>
              <Select
                className={styles.fullWidth}
                value={cfg.language}
                onChange={(v) => patch({ language: v })}
                options={SUPPORTED_LANGUAGES}
              />
              <p className={styles.fieldHelper}>
                {t("sonic.greeting_language_note", {
                  language: getLanguageLabel(cfg.language),
                })}
              </p>
            </div>
            <div className={styles.voiceSettingsField}>
              <label className={styles.fieldLabel}>{t("sonic.voice_engine")}</label>
              <Select
                className={styles.fullWidth}
                value={cfg.voice_engine || "auto"}
                onChange={(v) => patch({ voice_engine: v })}
                options={VOICE_ENGINE_OPTIONS.map((opt) => ({
                  value: opt.value,
                  label: t(opt.labelKey),
                }))}
              />
              <p className={styles.fieldHelper}>
                {cfg.voice_engine === "auto"
                  ? t("sonic.voice_engine_auto_hint", {
                      engine:
                        resolvedVoiceEngine === "elevenlabs"
                          ? t("sonic.voice_engine_elevenlabs")
                          : t("sonic.voice_engine_gemini"),
                    })
                  : t("sonic.voice_engine_fixed_hint")}
              </p>
            </div>
          </div>

          <label className={styles.fieldLabel}>{t("sonic.brand_name")}</label>
          <Input
            value={cfg.brand_name}
            maxLength={80}
            placeholder={t("sonic.brand_name_ph")}
            onChange={(e) => patch({ brand_name: e.target.value })}
          />
          <p className={styles.fieldHelper}>{t("sonic.brand_name_helper")}</p>

          <div className={styles.greetingHeaderRow}>
            <label className={styles.fieldLabel}>{t("sonic.greeting_script")}</label>
            <Tooltip
              title={
                canPreviewSpeech
                  ? t("sonic.tts_preview_hint_elevenlabs")
                  : t("sonic.tts_unsupported")
              }
            >
              <Button
                size="small"
                type="default"
                icon={previewSpeaking ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                disabled={!canPreviewSpeech}
                onClick={toggleSpeechPreview}
              >
                {previewSpeaking ? t("sonic.tts_stop") : t("sonic.listen_greeting")}
              </Button>
            </Tooltip>
          </div>
          {languagesMayMismatch(cfg.greeting, cfg.language) && (
            <span className={styles.languageMismatchTag}>
              <BrandTag tone="pink">{t("sonic.language_mismatch_warning")}</BrandTag>
            </span>
          )}
          <TextArea
            rows={4}
            value={cfg.greeting}
            placeholder={t("sonic.greeting_placeholder")}
            onChange={(e) => patch({ greeting: e.target.value })}
          />
          <p
            className={[
              styles.speechDurationHint,
              greetingTooLong ? styles.speechDurationWarn : "",
            ].join(" ")}
          >
            {t("sonic.speech_duration_estimate", {
              seconds: greetingDuration.seconds,
              chars: greetingDuration.chars,
            })}
            {greetingTooLong && (
              <span className={styles.speechDurationOver}>
                {" "}
                {t("sonic.speech_duration_over_limit", { max: GREETING_MAX_SECONDS })}
              </span>
            )}
          </p>
          <p className={styles.fieldHelper}>{t("sonic.greeting_helper")}</p>
          {showElevenLabsPicker && (
            <>
              <label className={styles.fieldLabel}>{t("sonic.elevenlabs_voice")}</label>
              {voicesLoading ? (
                <Spin size="small" />
              ) : voicesLoadError ? (
                <Alert
                  type={voicesLoadError.subscription ? "warning" : "error"}
                  showIcon
                  className={styles.voiceInlineAlert}
                  message={voicesLoadError.message}
                />
              ) : elevenLabsVoices.length === 0 ? (
                <p className={styles.fieldHelper}>{t("sonic.elevenlabs_voices_empty")}</p>
              ) : (
                <>
                  <Select
                    showSearch
                    className={styles.fullWidth}
                    placeholder={t("sonic.elevenlabs_voice_placeholder")}
                    value={cfg.elevenlabs_voice_id || undefined}
                    loading={voicesLoading}
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toString()
                        .toLowerCase()
                        .includes(input.trim().toLowerCase())
                    }
                    onChange={(voiceId) => patch({ elevenlabs_voice_id: voiceId })}
                    options={elevenLabsVoiceOptions}
                  />
                  <p className={styles.fieldHelper}>
                    {t("sonic.elevenlabs_voice_count", {
                      total: elevenLabsVoices.length,
                      language: getLanguageLabel(cfg.language),
                    })}
                  </p>
                  {selectedElevenLabsVoice ? (
                    <div className={styles.voiceSelectedPanel}>
                      <div className={styles.voiceSelectedHeader}>
                        <div>
                          <div className={styles.voiceSelectedName}>
                            {selectedElevenLabsVoice.name}
                          </div>
                          {selectedElevenLabsVoice.gender ? (
                            <Tag className={styles.voiceSelectedTag}>
                              {selectedElevenLabsVoice.gender}
                            </Tag>
                          ) : null}
                        </div>
                      </div>
                      {selectedElevenLabsVoice.description ? (
                        <p className={styles.voiceSelectedDesc}>
                          {selectedElevenLabsVoice.description}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}
              <p className={styles.fieldHelper}>{t("sonic.elevenlabs_voice_hint")}</p>
            </>
          )}
          {resolvedVoiceEngine === "gemini_live" && (
            <p className={styles.fieldHelper}>{t("sonic.voice_engine_gemini_hint")}</p>
          )}
        </div>

        <h4 className={styles.voiceSectionTitle}>{t("sonic.voice_section_behavior")}</h4>
        <div className={styles.previewCard}>
          <div className={styles.toggleRow}>
            <div>
              <div className={styles.toggleTitle}>{t("sonic.escalation_rules")}</div>
              <div className={styles.toggleDesc}>{t("sonic.escalation_desc_v2")}</div>
            </div>
            <Switch
              checked={cfg.escalate_to_human}
              onChange={(v) => patch({ escalate_to_human: v })}
            />
          </div>
          {cfg.escalate_to_human && (
            <div className={styles.nestedField}>
              <label className={styles.fieldLabel}>{t("sonic.transfer_number")}</label>
              <Input
                value={cfg.transfer_number}
                placeholder="+1 555 0100"
                onChange={(e) => patch({ transfer_number: e.target.value })}
              />
            </div>
          )}
        </div>

        <div className={styles.previewCard}>
          <div className={styles.toggleRow}>
            <div>
              <div className={styles.toggleTitle}>{t("sonic.consent_logging")}</div>
              <div className={styles.toggleDesc}>{t("sonic.consent_desc")}</div>
              <Button
                type="link"
                className={styles.inlineLinkBtn}
                onClick={() =>
                  history.push("/console/sonic?tab=settings&focus=compliance")
                }
              >
                {t("sonic.compliance_settings_link")}
              </Button>
            </div>
            <Switch
              checked={cfg.consent_logging}
              onChange={(v) => patch({ consent_logging: v })}
            />
          </div>
        </div>

        <h4 className={styles.voiceSectionTitle}>{t("sonic.voice_section_limits")}</h4>
        <div className={styles.previewCard}>
          <div className={styles.toggleRow}>
            <div>
              <div className={styles.toggleTitle}>{t("sonic.calling_hours_enabled")}</div>
              <div className={styles.toggleDesc}>{t("sonic.calling_hours_desc")}</div>
            </div>
            <Switch
              checked={cfg.calling_hours_enabled}
              onChange={(v) => patch({ calling_hours_enabled: v })}
            />
          </div>
          {cfg.calling_hours_enabled && (
            <div className={styles.callHoursRow}>
              <div className={styles.nestedField}>
                <label className={styles.fieldLabel}>{t("sonic.calling_hours_start")}</label>
                <Input
                  type="time"
                  value={cfg.calling_hours_start}
                  onChange={(e) => patch({ calling_hours_start: e.target.value || "09:00" })}
                  className={styles.fullWidth}
                />
              </div>
              <div className={styles.nestedField}>
                <label className={styles.fieldLabel}>{t("sonic.calling_hours_end")}</label>
                <Input
                  type="time"
                  value={cfg.calling_hours_end}
                  onChange={(e) => patch({ calling_hours_end: e.target.value || "20:00" })}
                  className={styles.fullWidth}
                />
              </div>
            </div>
          )}
          <p className={styles.fieldHelper}>{t("sonic.calling_hours_timezone_note")}</p>
          <div className={styles.toggleRow} style={{ marginTop: 14 }}>
            <div>
              <div className={styles.toggleTitle}>{t("sonic.exclude_weekends")}</div>
            </div>
            <Switch
              checked={cfg.exclude_weekends}
              onChange={(v) => patch({ exclude_weekends: v })}
            />
          </div>
          <div className={styles.toggleRow} style={{ marginTop: 14 }}>
            <div>
              <div className={styles.toggleTitle}>{t("sonic.exclude_public_holidays")}</div>
              <div className={styles.toggleDesc}>{t("sonic.exclude_holidays_desc")}</div>
            </div>
            <Switch
              checked={cfg.exclude_public_holidays}
              onChange={(v) => patch({ exclude_public_holidays: v })}
            />
          </div>
          <label className={styles.fieldLabel} style={{ marginTop: 14 }}>
            {t("sonic.max_attempts_per_contact")}
          </label>
          <div className={styles.callHoursRow}>
            <InputNumber
              min={1}
              max={99}
              value={cfg.max_attempts_per_contact}
              onChange={(v) => patch({ max_attempts_per_contact: v || 1 })}
              className={styles.fullWidth}
            />
            <Select
              value={cfg.max_attempts_period}
              onChange={(v) => patch({ max_attempts_period: v })}
              className={styles.fullWidth}
              options={MAX_ATTEMPT_PERIOD_OPTIONS.map((o) => ({
                value: o.value,
                label: t(o.labelKey),
              }))}
            />
          </div>
          <label className={styles.fieldLabel}>{t("sonic.max_call_duration")}</label>
          <Select
            className={styles.fullWidth}
            value={cfg.max_call_duration}
            onChange={(v) => patch({ max_call_duration: v })}
            options={DURATION_OPTIONS.map((seconds) => ({
              value: seconds,
              label: seconds
                ? t("sonic.duration_minutes", { count: seconds / 60 })
                : t("sonic.duration_no_limit"),
            }))}
          />
          <div className={styles.toggleRow} style={{ marginTop: 14 }}>
            <div>
              <div className={styles.toggleTitle}>{t("sonic.retry_no_answer")}</div>
            </div>
            <Switch
              checked={cfg.retry_enabled}
              onChange={(v) => patch({ retry_enabled: v })}
            />
          </div>
          {cfg.retry_enabled && (
            <div className={styles.nestedField}>
              <label className={styles.fieldLabel}>{t("sonic.retry_after_hours")}</label>
              <InputNumber
                min={1}
                max={72}
                value={cfg.retry_hours}
                onChange={(v) => patch({ retry_hours: v || 4 })}
                className={styles.fullWidth}
              />
            </div>
          )}
        </div>

        <Button type="primary" loading={saving} onClick={save}>
          {t("sonic.save_voice_agent")}
        </Button>
      </div>

      <div className={`${styles.testCard} ${styles.sonicPanel}`} ref={testCardRef}>
        <div className={styles.testCardHeader}>
          <span className={styles.testCardTitle}>
            <ThunderboltOutlined /> {t("sonic.test_call_title")}
          </span>
          <Tooltip title={t("sonic.test_call_tooltip")}>
            <InfoCircleOutlined className={styles.testInfoIcon} />
          </Tooltip>
        </div>

        <div className={styles.testCardBody}>
          <Segmented
            block
            value={testModeValue}
            onChange={(v) => setTestMode(v)}
            disabled={live || starting}
            className={styles.testModeSwitch}
            options={[
              { value: "phone", label: t("sonic.test_mode_phone"), icon: <PhoneOutlined /> },
              { value: "browser", label: t("sonic.test_mode_browser"), icon: <AudioOutlined /> },
            ]}
          />
          {testModeValue === "browser" && (
            <p className={styles.fieldHelper}>{t("sonic.browser_test_helper")}</p>
          )}

          {surveys.length > 0 && (
            <>
              <label className={styles.fieldLabel}>{t("sonic.call_survey")}</label>
              <Select
                allowClear
                value={form.instrumentId}
                onChange={(v) => patchForm({ instrumentId: v })}
                placeholder={t("sonic.call_survey_ph")}
                className={styles.fullWidth}
              >
                {surveys.map((s) => (
                  <Option key={s.id} value={s.id}>
                    {s.title}
                  </Option>
                ))}
              </Select>
              <p className={styles.fieldHelper}>{t("sonic.call_survey_helper")}</p>
            </>
          )}

          <label className={styles.fieldLabel}>{t("sonic.conversation_context")}</label>
          <TextArea
            rows={3}
            value={form.context}
            placeholder={t("sonic.conversation_context_ph_v2")}
            onChange={(e) => patchForm({ context: e.target.value })}
          />
          <p className={styles.fieldHelper}>{t("sonic.conversation_context_helper")}</p>

          <label className={styles.fieldLabel}>{t("sonic.call_language")}</label>
          <Select
            value={form.callLanguage || cfg.language}
            onChange={(v) => patchForm({ callLanguage: v })}
            className={styles.fullWidth}
            options={CALL_LANGUAGES}
          />
          <p className={styles.fieldHelper}>{t("sonic.call_language_helper")}</p>

          <label className={styles.fieldLabel}>{t("sonic.tone_persona")}</label>
          <Select
            value={form.persona}
            onChange={(v) => patchForm({ persona: v })}
            className={styles.fullWidth}
          >
            {PERSONA_OPTIONS.map((p) => (
              <Option key={p.value} value={p.value}>
                {t(p.labelKey)}
              </Option>
            ))}
          </Select>

          {testModeValue === "phone" && (
            <>
          <label className={styles.fieldLabel}>{t("sonic.verified_test_number")}</label>
          {numbers.length > 0 ? (
            <div className={styles.verifiedNumberRow}>
              <Select
                value={form.phone}
                onChange={(v) => patchForm({ phone: v })}
                placeholder={t("sonic.verified_test_number")}
                className={styles.fullWidth}
                optionLabelProp="label"
              >
                {numbers.map((n) => (
                  <Option
                    key={n.phone}
                    value={n.phone}
                    label={n.label ? `${n.label} (${n.phone})` : n.phone}
                  >
                    <span className={styles.numberOption}>
                      <span>{n.label ? `${n.label} (${n.phone})` : n.phone}</span>
                      <CheckCircleFilled className={styles.verifiedIcon} />
                    </span>
                  </Option>
                ))}
              </Select>
            </div>
          ) : (
            <div className={styles.noNumbers}>{t("sonic.no_verified_numbers_short")}</div>
          )}
          <Button
            type="link"
            size="small"
            icon={<PlusOutlined />}
            className={styles.addNumberLink}
            onClick={openVerify}
          >
            {t("sonic.add_verify_number")}
          </Button>

          <Checkbox
            checked={form.consent}
            onChange={(e) => patchForm({ consent: e.target.checked })}
            className={styles.consentRow}
          >
            {t("sonic.consent_confirm")}
          </Checkbox>
            </>
          )}

          <div className={styles.quotaBarWrap}>
            <Progress
              percent={quotaPct}
              showInfo={false}
              strokeColor="var(--color3)"
              trailColor="rgba(140, 140, 140, 0.25)"
              size="small"
            />
            <span className={styles.quotaHint}>
              {t("sonic.test_calls_remaining", {
                remaining: quota.test_calls_remaining,
                max: quota.test_calls_max,
              })}
            </span>
          </div>

          {callInFlight ? (
            <div className={styles.callStatusPanel}>
              {starting && (
                <div className={styles.callStatusRow}>
                  <span className={styles.callStatusDotPending} />
                  {t("sonic.call_initiating")}
                </div>
              )}
              {!starting && live && (
                <>
                  <div className={styles.callStatusRow}>
                    <span className={styles.callStatusDotActive} />
                    {t("sonic.browser_live", { time: formatCallDuration(callElapsed) })}
                  </div>
                  <Button danger block icon={<StopOutlined />} onClick={stopBrowserTest}>
                    {t("sonic.browser_stop")}
                  </Button>
                </>
              )}
              {!starting && !live && call && call.status === "in_progress" && (
                <div className={styles.callStatusRow}>
                  <span className={styles.callStatusDotActive} />
                  {call.mode === "browser"
                    ? t("sonic.browser_analyzing")
                    : t("sonic.call_in_progress", {
                        time: formatCallDuration(callElapsed),
                      })}
                </div>
              )}
            </div>
          ) : testModeValue === "browser" ? (
            <Tooltip title={browserTestEnabled ? "" : t("sonic.voice_coming_soon")}>
              <Button
                type="primary"
                block
                icon={<AudioOutlined />}
                disabled={!canStartBrowser}
                onClick={runBrowserTest}
                className={styles.startTestBtn}
              >
                {t("sonic.browser_start")}
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title={voiceTestEnabled ? "" : t(callUnavailableKey(callUnavailableReason))}>
              <Button
                type="primary"
                block
                icon={<PhoneOutlined />}
                loading={starting}
                disabled={!canStart}
                onClick={runTestCall}
                className={styles.startTestBtn}
              >
                {t("sonic.start_test_call")}
              </Button>
            </Tooltip>
          )}

          {call && DONE_STATES.includes(call.status) && (
            <div className={styles.callStatusPanel}>
              <div className={styles.callStatusEnded}>
                {t("sonic.call_ended", {
                  duration: formatCallDuration(call.duration_seconds || callElapsed),
                })}
                {call.transcript && (
                  <Button type="link" size="small">
                    {t("sonic.view_transcript")}
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className={styles.transcriptPanel}>{renderTranscript()}</div>
        </div>
      </div>
    </div>

      <Modal
        title={null}
        open={verify.open}
        onCancel={closeVerify}
        footer={null}
        destroyOnClose
        width={460}
        wrapClassName={styles.verifyModalWrap}
        centered
      >
        <div className={styles.verifyModalBody}>
          <div className={styles.verifyModalIcon}>
            <PhoneOutlined />
          </div>
          <h4 className={styles.verifyModalTitle}>{t("sonic.add_verify_number")}</h4>
          <p className={styles.verifyModalSubtitle}>
            {verify.step === "phone"
              ? t("sonic.verify_modal_subtitle")
              : t("sonic.enter_code_hint", { phone: verify.phone })}
          </p>

          <div className={styles.verifyStepper}>
            <div
              className={`${styles.verifyStep} ${
                verify.step === "phone" ? styles.verifyStepActive : styles.verifyStepDone
              }`}
            >
              <span className={styles.verifyStepCircle}>
                {verify.step === "code" ? "✓" : "1"}
              </span>
              <span className={styles.verifyStepLabel}>{t("sonic.verify_step_number")}</span>
            </div>
            <span
              className={`${styles.verifyStepLine} ${
                verify.step === "code" ? styles.verifyStepLineDone : ""
              }`}
            />
            <div
              className={`${styles.verifyStep} ${
                verify.step === "code" ? styles.verifyStepActive : styles.verifyStepIdle
              }`}
            >
              <span className={styles.verifyStepCircle}>2</span>
              <span className={styles.verifyStepLabel}>{t("sonic.verify_step_code")}</span>
            </div>
          </div>

        {verify.step === "phone" ? (
          <>
            <label className={styles.verifyFieldLabel}>{t("sonic.verify_phone_label")}</label>
            <div className={styles.customPhoneInput}>
              <PhoneInput
                international
                defaultCountry={defaultPhoneCountry}
                countryCallingCodeEditable={false}
                placeholder={t("sonic.phone_input_ph")}
                value={verify.phone}
                onChange={(v) => setVerify((s) => ({ ...s, phone: v || "" }))}
              />
            </div>
            <p className={styles.verifyHelper}>{t("sonic.sms_verify_helper")}</p>
            <label className={styles.verifyFieldLabel}>{t("sonic.number_label_optional")}</label>
            <Input
              className={styles.verifyModalInput}
              value={verify.label}
              placeholder={t("sonic.number_label_ph")}
              onChange={(e) => setVerify((v) => ({ ...v, label: e.target.value }))}
            />
            <Button
              id="sonic-voice-verify-btn"
              type="primary"
              block
              loading={verify.sending}
              disabled={!verify.phone}
              onClick={sendCode}
              className={`${styles.modalBtn} ${styles.verifyModalBtn}`}
            >
              {t("sonic.send_code")}
            </Button>
          </>
        ) : (
          <>
            <div className={styles.verifyCodeInput}>
              <ReactInputVerificationCode
                value={verify.code}
                onChange={(val) => setVerify((v) => ({ ...v, code: val }))}
                length={6}
                placeholder=""
                autoFocus
              />
            </div>
            <div className={styles.verifyTimerRow}>
              <ClockCircleOutlined
                className={
                  verify.timeLeft < 30
                    ? styles.verifyTimerWarn
                    : styles.verifyTimerIcon
                }
              />
              <span>
                {t("sonic.code_expires_in", {
                  time: formatTime(verify.timeLeft),
                })}
              </span>
            </div>
            <Button
              type="primary"
              block
              loading={verify.confirming}
              disabled={verify.code.trim().length < 6}
              onClick={confirmCode}
              className={`${styles.modalBtn} ${styles.verifyModalBtn}`}
            >
              {t("sonic.verify")}
            </Button>
            <div className={styles.verifyResendRow}>
              {verify.timeLeft === 0 ? (
                <button
                  type="button"
                  className={styles.verifyResendBtn}
                  onClick={resendCode}
                >
                  {t("sonic.resend_code")}
                </button>
              ) : (
                <span className={styles.verifyResendCooldown}>
                  {t("sonic.resend_in", { time: formatTime(verify.timeLeft) })}
                </span>
              )}
            </div>
            <button
              type="button"
              className={styles.verifyChangeNumber}
              onClick={backToPhoneStep}
            >
              {t("sonic.change_number")}
            </button>
          </>
        )}
        </div>
      </Modal>
    </div>
  );
};

export default VoiceAgentTab;
